import { type RefObject, useEffect, useState } from 'react';

/**
 * Опции хука {@link useScrollSpy}.
 */
type ScrollSpyOptions = {
  /**
   * `rootMargin` для `IntersectionObserver`. По умолчанию `'0px 0px -70% 0px'` —
   * секция считается активной, как только её верх попадает в первые 30%
   * видимой области контейнера. Менять, если нужна другая «зона активности».
   */
  rootMargin?: string;
  /** Пороги пересечения для `IntersectionObserver`. */
  threshold?: number | number[];
};

/**
 * Хук-«scrollspy»: отслеживает, какая из переданных секций сейчас активна
 * (видима в «зоне активности» скроллящегося контейнера).
 *
 * Использует один `IntersectionObserver` на все секции — без `scroll`-листенеров,
 * без чтения `getBoundingClientRect()` в реальном времени. Активной считается
 * первая по document-order секция, у которой `isIntersecting=true`. Если ни одна
 * не пересекается, возвращает последнюю активную — это удобно при скролле выше
 * первой секции.
 *
 * Поиск элементов идёт через `containerRef.current.querySelector` по DOM `id`,
 * экранированному через `CSS.escape`, чтобы поддерживать произвольные id
 * (включая точки, скобки и спец-символы).
 *
 * @param sectionIds Упорядоченный список DOM `id` секций; порядок задаёт
 *                   приоритет при выборе активной (первая по order, что в зоне).
 * @param containerRef Ref на скроллящийся контейнер (он же `root` обсервера).
 */
export const useScrollSpy = (
  sectionIds: string[],
  containerRef: RefObject<HTMLElement | null>,
  { rootMargin = '0px 0px -70% 0px', threshold = 0 }: ScrollSpyOptions = {},
): string | null => {
  const [activeId, setActiveId] = useState<string | null>(sectionIds[0] ?? null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || sectionIds.length === 0) {
      setActiveId(sectionIds[0] ?? null);
      return;
    }

    const visible = new Set<string>();
    // rAF-throttle: коалесцируем несколько срабатываний IO за один кадр
    // в одно обновление state, чтобы не блокировать скролл-кадры лишними
    // ререндерами (особенно заметно с открытыми DevTools).
    let rafId: number | null = null;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = entry.target.id;
          if (entry.isIntersecting) visible.add(id);
          else visible.delete(id);
        }
        if (rafId !== null) return;
        rafId = requestAnimationFrame(() => {
          rafId = null;
          // Берём ПОСЛЕДНЮЮ видимую по document-order — наиболее глубокую/специфичную.
          // При вложенных секциях родитель всегда пересекает «зону активности»
          // (его bbox охватывает весь контент детей), поэтому `find` всегда вернул бы
          // родителя и листы никогда не становились бы активными.
          const next = sectionIds.findLast((id) => visible.has(id));
          if (next) setActiveId(next);
        });
      },
      { root: container, rootMargin, threshold },
    );

    for (const id of sectionIds) {
      const el = container.querySelector<HTMLElement>(`#${CSS.escape(id)}`);
      if (el) observer.observe(el);
    }
    return () => {
      observer.disconnect();
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [sectionIds, containerRef, rootMargin, threshold]);

  return activeId;
};
